// HomePage — public landing.
// Phase 17.9g (Public Site Refresh) — copy swap to commercial,
// outcome-led, evidence-anchored tone. Preserves the existing
// PublicLayout chrome (navy/steel palette, .section / .section__inner
// / .cap-card / .hero classes, eyebrow labels, kpi-strip, scroll-
// reveal). All copy in this file is verbatim from the PM brief.
//
// Section order (8 sections):
//   1. Hero
//   2. Why It Exists
//   3. What the Platform Enables (4 capability cards)
//   4. Evidence You Can Stand Behind (credibility)
//   5. Optimise Before You Build (design optimisation)
//   6. A Structured Decision Workflow (5-stage strip)
//   7. Who This Is For (4 audience bullets)
//   8. Evaluate Your System Before You Commit (closing CTA)
//
// Lead capture: existing BrochureRequestModal preserved unchanged.
// Button label retuned to "Request an Assessment" per PM brief.

import { Link } from "react-router-dom";
import { useState } from "react";
import SeoHead from "@/components/SeoHead";
import BrochureRequestModal from "@/components/marketing/BrochureRequestModal";
import {
    buildBreadcrumbListSchema,
    buildSoftwareApplicationSchema,
} from "@/lib/schema";
import {
    ArrowRight,
    BookOpenCheck,
    ClipboardCheck,
    Cpu,
    FileCheck2,
    Gauge,
    LineChart,
    Microscope,
    Radar,
    Repeat,
    ShieldCheck,
    Sliders,
    Target as TargetIcon,
} from "lucide-react";

// ──────────────────────────────────────────────────────────────────
// Section 3 — "What the Platform Enables" (4 capability cards)
// ──────────────────────────────────────────────────────────────────
const PILLARS = [
    {
        icon: <Radar size={20} />,
        title: "Simulate Real Intercept Scenarios",
        body:
            "JSBSim-backed 6-DOF flight models allow realistic engagement modelling across target and interceptor profiles.",
        testId: "home-pillar-simulate",
    },
    {
        icon: <Microscope size={20} />,
        title: "Understand What Drives Success",
        body:
            "Every parameter is exposed, allowing identification of which factors materially influence probability of intercept.",
        testId: "home-pillar-drivers",
    },
    {
        icon: <Repeat size={20} />,
        title: "Reproduce and Defend Results",
        body:
            "Every run is deterministic and fully reproducible, with a complete audit trail of all inputs and assumptions.",
        testId: "home-pillar-reproduce",
    },
    {
        icon: <Sliders size={20} />,
        title: "Optimise System Design",
        body:
            "Forward and inverse simulation identifies which configuration changes will improve performance, with quantified confidence.",
        testId: "home-pillar-optimise",
    },
];

// ──────────────────────────────────────────────────────────────────
// Section 7 — "Who This Is For" (4 audience bullets)
// ──────────────────────────────────────────────────────────────────
const AUDIENCES = [
    {
        icon: <ClipboardCheck size={20} />,
        title: "Defence procurement teams",
        body: "Evaluating counter-UAS capability before commitment.",
        testId: "home-audience-procurement",
    },
    {
        icon: <Cpu size={20} />,
        title: "System integrators",
        body: "Designing or refining interceptor systems.",
        testId: "home-audience-integrators",
    },
    {
        icon: <TargetIcon size={20} />,
        title: "Operational planners",
        body: "Assessing real-world engagement scenarios.",
        testId: "home-audience-planners",
    },
    {
        icon: <LineChart size={20} />,
        title: "R&D teams",
        body: "Validating system performance before testing.",
        testId: "home-audience-rd",
    },
];

// ──────────────────────────────────────────────────────────────────
// Section 6 — "A Structured Decision Workflow" (5 stages)
// ──────────────────────────────────────────────────────────────────
const WORKFLOW = [
    { label: "Plan",     body: "Define scenario, threats, and system configuration." },
    { label: "Run",      body: "Execute deterministic and probabilistic simulations." },
    { label: "Analyse",  body: "Quantify outcomes and identify performance drivers." },
    { label: "Review",   body: "Validate assumptions and evidence." },
    { label: "Promote",  body: "Produce defensible outputs for decision-making." },
];

// ──────────────────────────────────────────────────────────────────
// Section 5 — "Optimise Before You Build" (3 outputs cards)
// ──────────────────────────────────────────────────────────────────
const DESIGN_OPT_OUTPUTS = [
    {
        icon: <Gauge size={20} />,
        title: "Probability of kill with confidence intervals",
        body: "Quantified Pk over Monte Carlo populations with bounded uncertainty.",
        testId: "home-design-output-pk",
    },
    {
        icon: <LineChart size={20} />,
        title: "Sensitivity to key parameters",
        body: "Variance decomposition identifies which inputs move outcomes.",
        testId: "home-design-output-sensitivity",
    },
    {
        icon: <Sliders size={20} />,
        title: "Recommended configuration changes",
        body: "Configuration deltas that lift performance, with design-cost trade-offs.",
        testId: "home-design-output-recommendations",
    },
];

// ──────────────────────────────────────────────────────────────────
// HomePage component
// ──────────────────────────────────────────────────────────────────
export default function HomePage() {
    const [assessmentOpen, setAssessmentOpen] = useState(false);
    return (
        <>
            <SeoHead
                path="/"
                title="UAVSimNet — Counter-UAS decisions you can defend"
                description="Defence-grade counter-UAS simulation. Quantify probability of success, identify the most effective system configurations, and back every result with a reproducible evidence chain."
                schemaJsonLd={[
                    buildSoftwareApplicationSchema(),
                    buildBreadcrumbListSchema({ path: "/", label: "Home" }),
                ]}
            />
            <div data-testid="home-page">
                {/* ───────── 1. HERO ───────── */}
                <section className="hero" data-testid="home-hero">
                    <div className="hero__inner">
                        <div>
                            <span className="hero__eyebrow">
                                <span className="dot" />
                                DEFENCE-GRADE COUNTER-UAS SIMULATION
                            </span>
                            <h1 className="hero__headline">
                                Make Counter-UAS Decisions<br />
                                <em>You Can Defend</em>
                            </h1>
                            <p className="hero__subhead">
                                UAVSimNet enables defence teams to simulate
                                real-world intercept scenarios, quantify probability
                                of success, and identify the most effective system
                                configurations before committing to procurement or
                                deployment.
                            </p>
                            <p
                                className="hero__subhead"
                                style={{ marginTop: 14 }}
                                data-testid="home-hero-secondary"
                            >
                                Every result is traceable, reproducible, and
                                backed by a structured evidence chain — ensuring
                                decisions stand up under technical, operational,
                                and procurement scrutiny.
                            </p>
                            <p
                                style={{
                                    marginTop: 22,
                                    fontFamily: "'JetBrains Mono', monospace",
                                    fontSize: 13,
                                    letterSpacing: "0.04em",
                                    color: "var(--ux-accent)",
                                }}
                                data-testid="home-hero-tagline"
                            >
                                Reduce risk. Eliminate assumption. Commit with confidence.
                            </p>
                            <div className="hero__cta-row" style={{ marginTop: 28 }}>
                                <button
                                    type="button"
                                    onClick={() => setAssessmentOpen(true)}
                                    className="btn-primary"
                                    data-testid="home-cta-hero-assessment"
                                >
                                    Request an Assessment <ArrowRight size={16} />
                                </button>
                                <Link
                                    to="/docs"
                                    className="btn-secondary"
                                    data-testid="home-cta-docs"
                                >
                                    See the docs
                                </Link>
                                <Link
                                    to="/demos"
                                    className="btn-secondary"
                                    data-testid="home-cta-live-demos"
                                >
                                    View live demos
                                </Link>
                            </div>
                        </div>
                        {/* Hero visual — preserved verbatim (workbench overview SVG).
                            Don't change the imagery; only the surrounding copy. */}
                        <div
                            data-testid="home-hero-visual"
                            style={{
                                border: "1px solid var(--ux-border-1)",
                                borderRadius: 10,
                                padding: 12,
                                background: "var(--ux-bg-soft, #11181f)",
                            }}
                        >
                            <img
                                src="/docs/screens/workbench_overview_redacted.png"
                                alt="Redacted Workbench overview — scenario tree, capability tabs, evidence rail. Identifiers and parameter values boxed out."
                                style={{ width: "100%", display: "block", borderRadius: 6 }}
                            />
                            <div
                                style={{
                                    marginTop: 8,
                                    color: "var(--ux-text-3)",
                                    fontSize: 11,
                                    fontFamily: "'JetBrains Mono', monospace",
                                    letterSpacing: "0.08em",
                                }}
                            >
                                WORKBENCH · scenario tree · capability tabs · evidence rail
                            </div>
                        </div>
                    </div>
                </section>

                {/* ───────── 2. WHY IT EXISTS ───────── */}
                <section className="section" data-testid="home-why">
                    <div className="section__inner">
                        <span className="section__eyebrow">// WHY IT EXISTS</span>
                        <h2 className="section__heading">
                            From assumption to evidence.
                        </h2>
                        <p className="section__lede">
                            Counter-UAS decisions are routinely made using limited
                            testing, vendor claims, and incomplete modelling.
                        </p>
                        <p
                            className="section__lede"
                            style={{ marginTop: 16 }}
                            data-testid="home-why-paragraph-2"
                        >
                            This leads to misaligned procurement, underperforming
                            systems, and operational risk that is only exposed
                            after deployment.
                        </p>
                        <p
                            className="section__lede"
                            style={{ marginTop: 16 }}
                            data-testid="home-why-paragraph-3"
                        >
                            UAVSimNet replaces assumption with evidence by making
                            every parameter visible, traceable, and defensible —
                            before a decision is made.
                        </p>
                    </div>
                </section>

                {/* ───────── 3. WHAT THE PLATFORM ENABLES ───────── */}
                <section
                    className="section section--alt"
                    data-testid="home-pillars"
                >
                    <div className="section__inner">
                        <span className="section__eyebrow">
                            // WHAT THE PLATFORM ENABLES
                        </span>
                        <h2 className="section__heading">
                            Four capabilities, end-to-end.
                        </h2>
                        <div className="cap-grid" style={{ marginTop: 32 }}>
                            {PILLARS.map((p, idx) => (
                                <div
                                    key={p.title}
                                    className="cap-card"
                                    data-delay={idx * 80}
                                    data-testid={p.testId}
                                >
                                    <div className="cap-card__icon">{p.icon}</div>
                                    <h3 className="cap-card__title">{p.title}</h3>
                                    <p className="cap-card__body">{p.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ───────── 4. EVIDENCE YOU CAN STAND BEHIND ───────── */}
                <section className="section" data-testid="home-credibility">
                    <div
                        className="section__inner"
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1.2fr",
                            gap: 48,
                            alignItems: "center",
                        }}
                    >
                        <div className="scroll-reveal">
                            <span className="section__eyebrow">
                                // EVIDENCE YOU CAN STAND BEHIND
                            </span>
                            <h2
                                className="section__heading"
                                style={{ fontSize: 36, lineHeight: 1.15 }}
                            >
                                Every parameter, sourced. Every result, frozen.
                            </h2>
                            <p
                                className="section__lede"
                                style={{ marginTop: 18 }}
                                data-testid="home-credibility-lead"
                            >
                                Every parameter in UAVSimNet carries:
                            </p>
                            <ul
                                className="prose-list"
                                style={{ marginTop: 12, paddingLeft: 22 }}
                                data-testid="home-credibility-input-bullets"
                            >
                                <li>a defined value and unit</li>
                                <li>a recorded source</li>
                                <li>a confidence classification</li>
                                <li>a supporting citation</li>
                            </ul>
                            <p
                                className="section__lede"
                                style={{ marginTop: 22 }}
                                data-testid="home-credibility-trailing"
                            >
                                All inputs are frozen into an immutable run
                                snapshot, ensuring that:
                            </p>
                            <ul
                                className="prose-list"
                                style={{ marginTop: 12, paddingLeft: 22 }}
                                data-testid="home-credibility-outcome-bullets"
                            >
                                <li>results cannot be altered retrospectively</li>
                                <li>every outcome is fully auditable</li>
                                <li>decisions can be justified under scrutiny</li>
                            </ul>
                            <div style={{ marginTop: 26 }}>
                                <Link
                                    to="/docs/evidence"
                                    className="btn-secondary"
                                    data-testid="home-credibility-cta"
                                >
                                    <FileCheck2 size={14} style={{ marginRight: 6 }} />
                                    How the evidence chain works
                                </Link>
                            </div>
                        </div>
                        <div
                            className="scroll-reveal"
                            data-delay="160"
                            style={{
                                border: "1px solid var(--ux-border-1)",
                                borderRadius: 10,
                                padding: 12,
                                background: "var(--ux-bg-soft, #11181f)",
                            }}
                        >
                            <img
                                src="/docs/diagrams/provenance_block.svg"
                                alt="Block diagram showing every parameter carrying source + confidence + citation into the immutable run snapshot."
                                style={{ width: "100%", display: "block", borderRadius: 6 }}
                            />
                            <div
                                style={{
                                    marginTop: 8,
                                    color: "var(--ux-text-3)",
                                    fontSize: 11,
                                    fontFamily: "'JetBrains Mono', monospace",
                                    letterSpacing: "0.08em",
                                }}
                            >
                                PROVENANCE · register · assemble · freeze on submit
                            </div>
                        </div>
                    </div>
                </section>

                {/* ───────── 5. OPTIMISE BEFORE YOU BUILD ───────── */}
                <section
                    className="section section--alt"
                    data-testid="home-design-optimisation"
                >
                    <div className="section__inner">
                        <span className="section__eyebrow">
                            // OPTIMISE BEFORE YOU BUILD
                        </span>
                        <h2 className="section__heading">
                            Find the right configuration on a workbench, not a range.
                        </h2>
                        <p className="section__lede">
                            UAVSimNet evaluates proposed interceptor configurations,
                            quantifies probability of success, and identifies which
                            design changes are most likely to improve outcomes
                            within defined constraints.
                        </p>
                        <p
                            className="section__lede"
                            style={{ marginTop: 18, fontSize: 14, color: "var(--ux-text-2)" }}
                            data-testid="home-design-outputs-lead"
                        >
                            Outputs include:
                        </p>
                        <div
                            className="cap-grid"
                            style={{ marginTop: 18, gridTemplateColumns: "repeat(3, 1fr)" }}
                            data-testid="home-design-optimisation-outputs"
                        >
                            {DESIGN_OPT_OUTPUTS.map((o, idx) => (
                                <div
                                    key={o.title}
                                    className="cap-card"
                                    data-delay={idx * 100}
                                    data-testid={o.testId}
                                >
                                    <div className="cap-card__icon">{o.icon}</div>
                                    <h3 className="cap-card__title">{o.title}</h3>
                                    <p className="cap-card__body">{o.body}</p>
                                </div>
                            ))}
                        </div>
                        <p
                            className="section__lede"
                            style={{ marginTop: 24 }}
                            data-testid="home-design-closing"
                        >
                            This enables teams to converge on effective designs
                            before physical development or testing.
                        </p>
                        <div style={{ marginTop: 18 }}>
                            <Link
                                to="/features"
                                className="btn-primary"
                                data-testid="home-design-optimisation-cta"
                            >
                                Explore the workbench <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* ───────── 6. A STRUCTURED DECISION WORKFLOW ───────── */}
                <section className="section" data-testid="home-workflow">
                    <div className="section__inner">
                        <span className="section__eyebrow">
                            // A STRUCTURED DECISION WORKFLOW
                        </span>
                        <h2 className="section__heading">
                            Plan → Run → Analyse → Review → Promote.
                        </h2>
                        <p
                            className="section__lede"
                            data-testid="home-workflow-lead"
                        >
                            UAVSimNet enforces a consistent workflow. Each stage
                            carries forward the same evidence and provenance.
                        </p>
                        <div
                            style={{ marginTop: 32 }}
                            data-testid="home-workflow-strip"
                        >
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(5, 1fr)",
                                    gap: 12,
                                }}
                            >
                                {WORKFLOW.map((stage, idx) => (
                                    <div
                                        key={stage.label}
                                        className="cap-card"
                                        style={{ padding: 20, position: "relative" }}
                                        data-testid={`home-workflow-step-${idx}`}
                                    >
                                        <div
                                            style={{
                                                fontFamily: "'JetBrains Mono', monospace",
                                                fontSize: 10,
                                                color: "var(--ux-accent)",
                                                letterSpacing: "0.12em",
                                                marginBottom: 6,
                                            }}
                                        >
                                            STAGE {String(idx + 1).padStart(2, "0")}
                                        </div>
                                        <div
                                            style={{
                                                fontFamily: "'JetBrains Mono', monospace",
                                                color: "var(--ux-text-1)",
                                                fontSize: 16,
                                                marginBottom: 8,
                                            }}
                                        >
                                            {stage.label}
                                        </div>
                                        <div
                                            style={{
                                                color: "var(--ux-text-3)",
                                                fontSize: 12,
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {stage.body}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ───────── 7. WHO THIS IS FOR ───────── */}
                <section
                    className="section section--alt"
                    data-testid="home-audiences"
                >
                    <div className="section__inner">
                        <span className="section__eyebrow">// WHO THIS IS FOR</span>
                        <h2 className="section__heading">
                            Built for the people whose decisions defend.
                        </h2>
                        <div
                            className="cap-grid"
                            style={{
                                marginTop: 32,
                                gridTemplateColumns: "repeat(4, 1fr)",
                            }}
                        >
                            {AUDIENCES.map((u, idx) => (
                                <div
                                    key={u.title}
                                    className="cap-card"
                                    data-delay={idx * 90}
                                    data-testid={u.testId}
                                >
                                    <div className="cap-card__icon">{u.icon}</div>
                                    <h3 className="cap-card__title">{u.title}</h3>
                                    <p className="cap-card__body">{u.body}</p>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: 24 }}>
                            <Link
                                to="/use-cases"
                                className="btn-ghost"
                                data-testid="home-audiences-cta"
                            >
                                <BookOpenCheck size={14} style={{ marginRight: 6 }} />
                                Read the use-case narratives
                            </Link>
                        </div>
                    </div>
                </section>

                {/* ───────── 8. EVALUATE BEFORE YOU COMMIT (final CTA) ───────── */}
                <section className="section" data-testid="home-cta-section">
                    <div
                        className="section__inner"
                        style={{ textAlign: "center" }}
                    >
                        <span className="section__eyebrow">// NEXT</span>
                        <h2 className="section__heading">
                            Evaluate Your System Before You Commit.
                        </h2>
                        <p
                            className="section__lede"
                            style={{ margin: "0 auto 18px", maxWidth: 760 }}
                            data-testid="home-cta-body"
                        >
                            Submit a scenario or system configuration and receive
                            a structured simulation-based assessment of performance,
                            limitations, and optimisation opportunities.
                        </p>
                        <p
                            className="section__lede"
                            style={{
                                margin: "0 auto 28px",
                                maxWidth: 760,
                                fontSize: 14,
                                color: "var(--ux-text-3)",
                            }}
                            data-testid="home-cta-access"
                        >
                            Access is provisioned per engagement to ensure
                            controlled, secure usage.
                        </p>
                        <div
                            style={{
                                display: "inline-flex",
                                gap: 12,
                                flexWrap: "wrap",
                                justifyContent: "center",
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => setAssessmentOpen(true)}
                                className="btn-primary"
                                data-testid="home-cta-bottom-assessment"
                            >
                                Request an Assessment <ArrowRight size={16} />
                            </button>
                            <Link
                                to="/docs"
                                className="btn-secondary"
                                data-testid="home-cta-bottom-docs"
                            >
                                Read the docs
                            </Link>
                            <Link
                                to="/security"
                                className="btn-ghost"
                                data-testid="home-cta-bottom-security"
                            >
                                <ShieldCheck size={14} style={{ marginRight: 6 }} />
                                Security posture
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
            {/* Existing CapsuleCRM-backed lead-capture modal — fields
                preserved, only the trigger label changes. */}
            <BrochureRequestModal
                open={assessmentOpen}
                onOpenChange={setAssessmentOpen}
            />
        </>
    );
}
